import { Product } from 'lib/enums';
import { removeUndefinedFields } from 'lib/helpers';
import { getModelFromSnapshot } from 'lib/model';
import { OrderModel } from 'lib/model/objects/orderModel';
import { UserModel } from 'lib/model/objects/userModel';
import { getCustomer } from 'lib/notice/customer';
import { safeAsync, safeGetOrThrow } from 'lib/safeWrappers';
import { ClassifiedService } from 'lib/services/classifiedService';
import { ColumnService } from 'lib/services/directory';
import { NewspaperOrderService } from 'lib/services/newspaperOrderService';
import { ObituaryService } from 'lib/services/obituaryService';
import { OrderDetailService } from 'lib/services/orderDetailService';
import { OrderService } from 'lib/services/orderService';
import { Customer, ERef, ESnapshotExists, EUser } from 'lib/types';
import { Classified } from 'lib/types/classified';
import { Obituary } from 'lib/types/obituary';
import {
  Order,
  isAdvertiserOrder,
  AdditionalAdvertiserWithOrganizationOrderInfo,
  AdditionalIndividualAdvertiserOrderInfo,
  AdditionalPublisherAsAnonymousOrderInfo,
  isPublisherAsAdvertiserOrder,
  isAnonymousOrder
} from 'lib/types/order';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { logAndCaptureException } from 'utils';
import { getFirebaseContext } from 'utils/firebase';

export const duplicateOrder = async (
  originalOrder: OrderModel,
  user: ESnapshotExists<EUser>
): Promise<ResponseOrError<ERef<Order>>> => {
  const ctx = getFirebaseContext();

  const { product } = originalOrder.modelData;

  const initialVersion = ctx.timestamp().toMillis();

  const userModel = getModelFromSnapshot(UserModel, ctx, user);

  const [createNewOrderRefError, newOrderRef] = await createNewDuplicatedOrder(
    originalOrder,
    product,
    userModel,
    initialVersion
  );

  if (createNewOrderRefError) {
    return wrapError(createNewOrderRefError);
  }

  if (product === Product.Classified) {
    const [errorDuplicatingClassified] = await duplicateClassifiedAd(
      originalOrder,
      newOrderRef,
      initialVersion
    );
    if (errorDuplicatingClassified) {
      return wrapError(errorDuplicatingClassified);
    }
  } else if (product === Product.Obituary) {
    const [errorDuplicatingObituary] = await duplicateObituaryAd(
      originalOrder,
      newOrderRef,
      initialVersion
    );
    if (errorDuplicatingObituary) {
      return wrapError(errorDuplicatingObituary);
    }
  }

  const [errorDuplicatingOrderDetails] = await duplicateOrderDetails(
    originalOrder,
    newOrderRef,
    initialVersion
  );

  if (errorDuplicatingOrderDetails) {
    return wrapError(errorDuplicatingOrderDetails);
  }

  const [errorDuplicatingNewspaperOrders] = await duplicateNewspaperOrders(
    originalOrder,
    newOrderRef,
    initialVersion
  );

  if (errorDuplicatingNewspaperOrders) {
    return wrapError(errorDuplicatingNewspaperOrders);
  }

  return wrapSuccess(newOrderRef);
};

const getCustomerFromAdvertiser = async (
  order: OrderModel,
  user: UserModel
): Promise<ResponseOrError<ERef<Customer> | undefined>> => {
  if (!user.isPublisher) {
    return wrapSuccess(undefined);
  }

  if (isPublisherAsAdvertiserOrder(order.modelData)) {
    return wrapSuccess(order.modelData.advertiserCustomer);
  }

  if (isAnonymousOrder(order.modelData)) {
    return wrapSuccess(undefined);
  }

  const [advertiserSnapError, advertiser] = await safeGetOrThrow(
    order.modelData.advertiser
  );
  if (advertiserSnapError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      advertiserSnapError,
      'Failed to get advertiser',
      { advertiserId: order.modelData.advertiser.id }
    );
    return wrapError(advertiserSnapError);
  }

  const [publisherError, publisher] = await safeGetOrThrow(
    user.modelData.activeOrganization
  );
  if (publisherError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      publisherError,
      'Failed to get publisher',
      { publisherId: user.modelData.activeOrganization?.id }
    );
    return wrapError(publisherError);
  }

  const [customerSnapError, customerSnap] = await safeAsync(() =>
    getCustomer(getFirebaseContext(), advertiser, publisher)
  )();
  if (customerSnapError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      customerSnapError,
      'Failed to get customer',
      { advertiserId: advertiser.id, publisherId: publisher.id }
    );
    return wrapError(customerSnapError);
  }

  if (!customerSnap) {
    return wrapSuccess(undefined);
  }

  return wrapSuccess(customerSnap.ref);
};

const createNewDuplicatedOrder = async (
  originalOrder: OrderModel,
  product: Product,
  userModel: UserModel,
  initialVersion: number
): Promise<ResponseOrError<ERef<Order>>> => {
  const [customerError, advertiserCustomer] = await getCustomerFromAdvertiser(
    originalOrder,
    userModel
  );
  if (customerError) {
    return wrapError(customerError);
  }

  const getOrderInfo = () => {
    if (isAdvertiserOrder(originalOrder.modelData)) {
      return {
        advertiser: originalOrder.modelData.advertiser,
        advertiserOrganization:
          originalOrder.modelData.advertiserOrganization || null,
        advertiserCustomer
      };
    }
    const orderContactInfo = {
      contactEmail: originalOrder.modelData.contactEmail,
      firstName: originalOrder.modelData.firstName,
      lastName: originalOrder.modelData.lastName,
      phone: originalOrder.modelData.phone
    };
    const classifiedOnlyContactInfo = {
      addressLine1: originalOrder.modelData.addressLine1,
      addressLine2: originalOrder.modelData.addressLine2,
      city: originalOrder.modelData.city,
      state: originalOrder.modelData.state,
      zip: originalOrder.modelData.zip,
      organizationName: originalOrder.modelData.organizationName
    };
    if (product === Product.Obituary) {
      return {
        ...orderContactInfo
      };
    }
    return {
      ...orderContactInfo,
      ...classifiedOnlyContactInfo
    };
  };

  const orderInfo = getOrderInfo();

  const extraOrderInfo:
    | AdditionalAdvertiserWithOrganizationOrderInfo
    | AdditionalIndividualAdvertiserOrderInfo
    | AdditionalPublisherAsAnonymousOrderInfo = {
    ...orderInfo,
    user: userModel.ref,
    authorizedOrganization: userModel.modelData.activeOrganization || null
  };

  const commonFields = removeUndefinedFields({
    ...extraOrderInfo,
    originalRef: originalOrder.ref
  });

  const orderService: OrderService = new OrderService(getFirebaseContext());

  const [createNewOrderRefError, newOrderRef] = await safeAsync(() =>
    orderService.create(commonFields, product, initialVersion)
  )();

  if (createNewOrderRefError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      createNewOrderRefError,
      'Failed to create new order',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(createNewOrderRefError);
  }

  return wrapSuccess(newOrderRef);
};

const duplicateObituaryAd = async (
  originalOrder: OrderModel,
  newOrderRef: ERef<Order>,
  initialVersion: number
) => {
  const [obituaryDataModelError, obituaryModel] =
    await originalOrder.getObituary(originalOrder.modelData.activeVersion);

  if (obituaryDataModelError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      obituaryDataModelError,
      'Failed to get obituary',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(obituaryDataModelError);
  }

  const obituaryModelData: Obituary = {
    ...obituaryModel.modelData,
    title: obituaryModel.modelData.title || '',
    order: newOrderRef
  };

  const obituaryService: ObituaryService = new ObituaryService(
    getFirebaseContext()
  );

  const duplicatingObituary = safeAsync(() =>
    obituaryService.cloneForEditOrDuplicateFlow(
      obituaryModel.id,
      obituaryModelData,
      initialVersion
    )
  );

  const [duplicateObituaryError] = await duplicatingObituary();

  if (duplicateObituaryError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      duplicateObituaryError,
      'Failed to duplicate obituary',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(duplicateObituaryError);
  }

  return wrapSuccess(undefined);
};

const duplicateClassifiedAd = async (
  originalOrder: OrderModel,
  newOrderRef: ERef<Order>,
  initialVersion: number
) => {
  const [classifiedModelError, classifiedModel] =
    await originalOrder.getClassified(originalOrder.modelData.activeVersion);

  if (classifiedModelError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      classifiedModelError,
      'Failed to get classified',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(classifiedModelError);
  }

  const classifiedModelData: Classified = {
    ...classifiedModel.modelData,
    title: classifiedModel.modelData.title || '',
    order: newOrderRef
  };

  const classifiedService: ClassifiedService = new ClassifiedService(
    getFirebaseContext()
  );

  const duplicatingClassified = safeAsync(() =>
    classifiedService.cloneForEditOrDuplicateFlow(
      classifiedModel.id,
      classifiedModelData,
      initialVersion
    )
  );

  const [duplicateClassifiedError] = await duplicatingClassified();

  if (duplicateClassifiedError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      duplicateClassifiedError,
      'Failed to duplicate classified',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(duplicateClassifiedError);
  }

  return wrapSuccess(undefined);
};

const duplicateOrderDetails = async (
  originalOrder: OrderModel,
  newOrderRef: ERef<Order>,
  initialVersion: number
) => {
  // duplicate orderDetails
  const orderDetailService: OrderDetailService = new OrderDetailService(
    getFirebaseContext()
  );
  const [orderDetailsModelError, orderDetailsModel] =
    await originalOrder.getOrderDetail();

  if (orderDetailsModelError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      orderDetailsModelError,
      'Failed to get order details',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(orderDetailsModelError);
  }

  if (!orderDetailsModel) {
    const error = new Error('Order details do not exist.');
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      error,
      'Failed to get order details.',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(error);
  }

  const duplicatingOrderDetails = safeAsync(() =>
    orderDetailService.cloneForEditOrDuplicateFlow(
      newOrderRef,
      orderDetailsModel,
      initialVersion
    )
  );

  const [duplicatingOrderDetailsError] = await duplicatingOrderDetails();

  if (duplicatingOrderDetailsError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      duplicatingOrderDetailsError,
      'Failed to duplicate order details',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(duplicatingOrderDetailsError);
  }

  return wrapSuccess(undefined);
};

const duplicateNewspaperOrders = async (
  originalOrder: OrderModel,
  newOrderRef: ERef<Order>,
  initialVersion: number
) => {
  // duplicate newspaperOrders
  const newspaperOrderService: NewspaperOrderService =
    new NewspaperOrderService(getFirebaseContext());

  const getNewspaperOrderModels = safeAsync(() =>
    originalOrder.getNewspaperOrders({
      specifiedVersion: originalOrder.modelData.activeVersion
    })
  );

  const [errorOnGettingNewspaperOrderModels, newspaperOrderModels] =
    await getNewspaperOrderModels();
  if (errorOnGettingNewspaperOrderModels) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      errorOnGettingNewspaperOrderModels,
      'Failed to get newspaper orders',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(errorOnGettingNewspaperOrderModels);
  }
  const duplicatingNewspaperOrders = safeAsync(() =>
    newspaperOrderService.cloneForEditOrDuplicateFlow(
      newOrderRef,
      newspaperOrderModels,
      initialVersion
    )
  );

  const [duplicatingNewspaperOrdersError] = await duplicatingNewspaperOrders();

  if (duplicatingNewspaperOrdersError) {
    logAndCaptureException(
      ColumnService.ORDER_PLACEMENT,
      duplicatingNewspaperOrdersError,
      'Failed to duplicate newspaper orders',
      { originalOrderId: originalOrder.id }
    );
    return wrapError(duplicatingNewspaperOrdersError);
  }

  return wrapSuccess(undefined);
};
